import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import Layout from './Layout';
import SmartImage from './SmartImage';
import { getCategoryIconClass } from '../utils/imageHelpers';
import { useToast } from '../contexts/ToastContext';

interface Product {
  id: number;
  sku: string;
  name: string;
  category: string;
  image_url: string;
  bundled_item?: string;  // PHD code of bundled item
}

interface OrderItem {
  id: string;
  product: Product;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes?: string;
  is_bundled_item?: boolean;
  bundled_with_product_id?: number;
}

interface TimelineEvent {
  event: string;
  timestamp: string;
  user?: string;
  details?: Record<string, any>;
}

interface PaymentInfo {
  transaction_id: string;
  status: string;
  method: string;
  amount: number;
  fee_amount?: number;
  payment_date: string;
  stripe_payment_intent_id?: string;
}

interface Order {
  id: string;
  order_number: string;
  user_id: number;
  siteid: number;
  sitename: string;
  customer_name: string;
  customer_email: string;
  status: string;
  type: string;
  payment_status: string;
  payment_method?: string;
  payment_date?: string;
  po_number?: string;
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  total_amount: number;
  notes?: string;
  reference?: string;
  created_at: string;
  updated_at: string;
  approved_at?: string;
  shipped_at?: string;
  delivered_at?: string;
}

interface OrderDetailResponse {
  order: Order;
  items: OrderItem[];
  payment_info?: PaymentInfo;
  timeline: TimelineEvent[];
}

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [orderData, setOrderData] = useState<OrderDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [timelineCollapsed, setTimelineCollapsed] = useState(true);
  const isDesktop = windowWidth >= 992;

  const fetchOrderDetail = useCallback(async () => {
    if (!orderId) return;

    setLoading(true);
    try {
      const response = await api.get<OrderDetailResponse>(`/orders/${orderId}`);

      // Sort items: group bundled items with their parent products (same logic as Cart)
      const rawItems = response.data.items || [];

      // Create a map of parent product IDs to their bundled items
      const bundledItemsMap = new Map<number, OrderItem[]>();
      const parentItems: OrderItem[] = [];

      // Separate parent items from bundled items
      rawItems.forEach((item: OrderItem) => {
        if (item.is_bundled_item && item.bundled_with_product_id) {
          // This is a bundled item
          const parentId = item.bundled_with_product_id;
          if (!bundledItemsMap.has(parentId)) {
            bundledItemsMap.set(parentId, []);
          }
          bundledItemsMap.get(parentId)!.push(item);
        } else {
          // This is a parent/normal item
          parentItems.push(item);
        }
      });

      // Sort parent items by price descending
      parentItems.sort((a, b) => b.unit_price - a.unit_price);

      // Build final sorted list: parent followed by its bundled items
      const sortedItems: OrderItem[] = [];
      parentItems.forEach(parent => {
        sortedItems.push(parent);
        // Add bundled items right after their parent
        const bundledItems = bundledItemsMap.get(parent.product.id) || [];
        sortedItems.push(...bundledItems);
      });

      const sortedData = {
        ...response.data,
        items: sortedItems
      };
      setOrderData(sortedData);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderDetail();
  }, [fetchOrderDetail]);

  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const handleReorder = async () => {
    if (!orderData) return;

    setReordering(true);

    try {
      // Use the new backend reorder endpoint
      const response = await api.post(`/orders/${orderId}/reorder`);
      const result = response.data;

      // Show success message with details
      if (result.items_added > 0) {
        showSuccess('Items added to cart successfully!');

        // Redirect to cart after a short delay
        setTimeout(() => {
          navigate('/cart');
        }, 1500);
      } else {
        showError('No items could be added to cart. Please check product availability.');
      }

      // Log any errors if present
      if (result.errors && result.errors.length > 0) {
        console.error('[REORDER] Errors encountered:', result.errors);
      }
    } catch (error) {
      console.error('Failed to reorder:', error);
      showError('Failed to add items to cart. Please try again.');
    } finally {
      setReordering(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}/${day}/${year} ${displayHours}:${minutes} ${ampm}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getShortOrderNumber = (orderNumber: string): string => {
    // Extract the last 4 characters from order number (e.g., ORD-20250909144547-CE46 -> ORD-CE46)
    const parts = orderNumber.split('-');
    if (parts.length >= 3) {
      return `ORD-${parts[parts.length - 1]}`;
    }
    return orderNumber;
  };

  const getStatusColor = (status: string): string => {
    const statusMap: Record<string, string> = {
      'draft': '#6c757d',
      'quote': '#6c757d',
      'pending': '#ef9221',
      'processing': '#0d6efd',
      'approved': '#198754',
      'shipped': '#198754',
      'delivered': '#198754',
      'cancelled': '#dc3545',
      'refunded': '#6c757d'
    };
    return statusMap[status.toLowerCase()] || '#6c757d';
  };

  const getPaymentStatusColor = (status: string): string => {
    const statusMap: Record<string, string> = {
      'pending': '#ef9221',
      'paid': '#198754',
      'processing': '#0d6efd',
      'succeeded': '#198754',
      'failed': '#dc3545',
      'cancelled': '#dc3545',
      'refunded': '#ef9221',
      'not_required': '#6c757d'
    };
    return statusMap[status.toLowerCase()] || '#6c757d';
  };

  // Truncate helper for consistent character-based truncation
  const truncateText = (text?: string, maxChars: number = 50): string => {
    if (!text) return '';
    if (text.length <= maxChars) return text;
    return text.slice(0, maxChars).trimEnd() + '…';
  };

  const getOrderTypeIcon = (type: string): string => {
    const typeMap: Record<string, string> = {
      'DIRECT_ORDER': 'credit-card',
      'PURCHASE_ORDER': 'file-invoice',
      'QUOTE': 'file-alt'
    };
    return typeMap[type] || 'shopping-cart';
  };

  const getPaymentMethodIcon = (method?: string): string => {
    if (!method) return 'question-circle';
    const methodMap: Record<string, string> = {
      'card': 'credit-card',
      'stripe': 'credit-card',
      'purchase_order': 'file-invoice',
      'po': 'file-invoice',
      'wire': 'university',
      'check': 'money-check'
    };
    return methodMap[method.toLowerCase()] || 'dollar-sign';
  };

  const getTimelineIcon = (event: string): string => {
    const eventLower = event.toLowerCase();
    if (eventLower.includes('created')) return 'plus-circle';
    if (eventLower.includes('payment')) return 'credit-card';
    if (eventLower.includes('approved')) return 'check-circle';
    if (eventLower.includes('shipped')) return 'truck';
    if (eventLower.includes('delivered')) return 'box-check';
    if (eventLower.includes('cancelled')) return 'times-circle';
    return 'info-circle';
  };

  const getTimelineColor = (event: string): string => {
    const eventLower = event.toLowerCase();
    if (eventLower.includes('created')) return 'primary';
    if (eventLower.includes('payment succeeded')) return 'success';
    if (eventLower.includes('payment failed')) return 'danger';
    if (eventLower.includes('approved')) return 'success';
    if (eventLower.includes('shipped')) return 'info';
    if (eventLower.includes('delivered')) return 'success';
    if (eventLower.includes('cancelled')) return 'danger';
    return 'secondary';
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading order details...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!orderData) {
    return (
      <Layout>
        <div className="order-detail-container" style={{ 
        maxWidth: '1400px', 
        margin: '0 auto',
        padding: '0 1.5rem'
      }}>
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Order not found or you don't have permission to view it.
          </div>
          <Link to="/orders" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i> Back to Orders
          </Link>
        </div>
      </Layout>
    );
  }

  const { order, items, payment_info, timeline } = orderData;

  return (
    <Layout>
      <div className="container">
        {/* Header */}
        <div className="row mb-4">
          <div className="col">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h1 className="h2 mb-2">
                  Order {getShortOrderNumber(order.order_number)}
                </h1>
              </div>
              
              <div className="text-end">
                <button
                  className="btn me-2"
                  onClick={handleReorder}
                  disabled={reordering}
                  style={{
                    backgroundColor: '#2E7D32',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {reordering ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Reordering...
                    </>
                  ) : (
                    <>
                      Reorder
                    </>
                  )}
                </button>
                {order.status === 'delivered' && (
                  <button className="btn btn-outline-secondary" disabled>
                    <i className="fas fa-file-invoice me-2"></i> Download Invoice
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={{ 
          display: 'flex',
          flexWrap: isDesktop ? 'nowrap' : 'wrap',
          gap: '30px'
        }}>
          {/* Main Content - Force proper width */}
          <div className="order-items-section" style={{
            flex: isDesktop ? '0 0 66.66667%' : '0 0 100%',
            maxWidth: isDesktop ? 'calc(66.66667% - 15px)' : '100%'
          }}>
            {/* Order Items */}
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-box me-2"></i> Order Items ({items.length})
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Product</th>
                        <th className="text-center">Quantity</th>
                        <th style={{ textAlign: 'right', paddingRight: '15px' }}>Unit Price</th>
                        <th style={{ width: '150px', textAlign: 'right', paddingRight: '20px' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id}>
                          <td style={{ verticalAlign: 'middle' }}>
                            <div className="d-flex align-items-center">
                              <div className="me-3" style={{
                                width: '60px',
                                height: '60px',
                                minWidth: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa'
                              }}>
                                <SmartImage
                                  src={item.product.image_url}
                                  alt={item.product.name}
                                  className="order-detail-image"
                                  productName={item.product.name}
                                  productCategory={item.product.category}
                                  productSku={item.product.sku}
                                  customFallback={
                                    <div className="d-flex align-items-center justify-content-center bg-light"
                                         style={{ width: '60px', height: '60px' }}>
                                      <i className={`${getCategoryIconClass(item.product)} fa-2x text-muted`}></i>
                                    </div>
                                  }
                                />
                              </div>
                              <div style={{ minHeight: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Link 
                                  to={`/products/${item.product.id}`} 
                                  className="text-decoration-none fw-bold"
                                  title={item.product.name}
                                  style={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '350px'
                                  }}
                                >
                                  {item.product.name}
                                </Link>
                                {item.notes && (
                                  <div className="small text-info mt-1">
                                    <i className="fas fa-sticky-note me-1"></i> {item.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            {item.quantity}
                          </td>
                          <td style={{ verticalAlign: 'middle', textAlign: 'right', paddingRight: '15px' }}>
                            {formatCurrency(item.unit_price)}
                          </td>
                          <td className="fw-bold" style={{ verticalAlign: 'middle', width: '150px', textAlign: 'right', paddingRight: '20px' }}>
                            {formatCurrency(item.total_price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan={3} className="text-end pe-3">Subtotal:</td>
                        <td className="fw-semibold" style={{ width: '150px', textAlign: 'right', paddingRight: '20px' }}>
                          {formatCurrency(order.subtotal)}
                        </td>
                      </tr>
                      {order.shipping_cost > 0 && (
                        <tr>
                          <td colSpan={3} className="text-end pe-3">Shipping:</td>
                          <td style={{ width: '150px', textAlign: 'right', paddingRight: '20px' }}>
                            {formatCurrency(order.shipping_cost)}
                          </td>
                        </tr>
                      )}
                      {order.tax_amount > 0 && (
                        <tr>
                          <td colSpan={3} className="text-end pe-3">Tax:</td>
                          <td style={{ width: '150px', textAlign: 'right', paddingRight: '20px' }}>
                            {formatCurrency(order.tax_amount)}
                          </td>
                        </tr>
                      )}
                      <tr className="fw-bold">
                        <td colSpan={3} className="text-end pe-3 fs-5">Total:</td>
                        <td className="text-primary fs-5" style={{ width: '150px', textAlign: 'right', paddingRight: '20px', borderTop: '2px solid #dee2e6' }}>
                          {formatCurrency(order.total_amount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            {/* Order Timeline */}
            <div className="card shadow-sm">
              <div 
                className="card-header bg-info text-white" 
                style={{ cursor: 'pointer' }}
                onClick={() => setTimelineCollapsed(!timelineCollapsed)}
              >
                <h5 className="mb-0 d-flex justify-content-between align-items-center">
                  <span>
                    <i className="fas fa-clock me-2"></i> Order Timeline
                  </span>
                  <i className={`fas fa-chevron-${timelineCollapsed ? 'down' : 'up'}`}></i>
                </h5>
              </div>
              {!timelineCollapsed && (
                <div className="card-body">
                <div className="timeline">
                  {timeline.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-marker">
                        <i className={`fas fa-${getTimelineIcon(event.event)} text-${getTimelineColor(event.event)}`}></i>
                      </div>
                      <div className="timeline-content">
                        <h6 className="mb-1">{event.event}</h6>
                        <p className="text-muted small mb-1">
                          {formatDate(event.timestamp)}
                        </p>
                        {event.user && (
                          <p className="small mb-1">By: {event.user}</p>
                        )}
                        {event.details && (
                          <div className="small">
                            {Object.entries(event.details)
                              .filter(([key]) => key.toLowerCase() !== 'type')
                              .map(([key, value]) => (
                                <div key={key}>
                                  <strong>{key.replace(/_/g, ' ')}:</strong> {
                                    typeof value === 'number' && key.includes('amount') 
                                      ? formatCurrency(value)
                                      : String(value)
                                  }
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          {/* Side panels - Force proper width */}
          <div className="order-info-section" style={{
            flex: isDesktop ? '0 0 33.33333%' : '0 0 100%',
            maxWidth: isDesktop ? 'calc(33.33333% - 15px)' : '100%'
          }}>
            {/* Order Status */}
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="fas fa-info-circle me-2"></i> Order Status
                </h5>
              </div>
              <div className="card-body">
                <div className="text-center">
                  <span style={{
                    color: '#000000',
                    fontWeight: '600',
                    fontSize: '1.2rem',
                    textTransform: 'uppercase'
                  }}>
                    {order.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="fas fa-file-alt me-2"></i> Order Details
                </h5>
              </div>
              <div className="card-body">
                <dl className="row mb-0">
                  <dt className="col-sm-4">Order Date:</dt>
                  <dd className="col-sm-8">{formatDate(order.created_at)}</dd>
                  
                  <dt className="col-sm-4">Customer:</dt>
                  <dd className="col-sm-8">
                    {order.customer_name}
                    <div className="small text-muted">{order.customer_email}</div>
                  </dd>
                  
                  <dt className="col-sm-4">Property:</dt>
                  <dd className="col-sm-8" title={order.sitename}>
                    {order.sitename}
                  </dd>
                  
                  {order.po_number && (
                    <>
                      <dt className="col-sm-4">PO Number:</dt>
                      <dd className="col-sm-8">{order.po_number}</dd>
                    </>
                  )}
                  
                  {/* Reference hidden from end users */}
                  
                  {order.notes && (
                    <>
                      <dt className="col-sm-4">Notes:</dt>
                      <dd className="col-sm-8" title={order.notes}>
                        {order.notes}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Styles */}
        <style>{`
          .timeline {
            position: relative;
            padding-left: 30px;
          }
          
          .timeline::before {
            content: '';
            position: absolute;
            left: 10px;
            top: 0;
            bottom: 0;
            width: 2px;
            background-color: #dee2e6;
          }
          
          .timeline-item {
            position: relative;
            padding-bottom: 30px;
          }
          
          .timeline-item:last-child {
            padding-bottom: 0;
          }
          
          .timeline-marker {
            position: absolute;
            left: -20px;
            top: 0;
            width: 20px;
            height: 20px;
            background-color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 0 0 3px #f8f9fa;
          }
          
          .timeline-content {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
          }
          
          .order-detail-image {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }

          @media (max-width: 768px) {
            .timeline {
              padding-left: 20px;
            }

            .timeline-marker {
              left: -10px;
              width: 16px;
              height: 16px;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </Layout>
  );
};

export default OrderDetail;